import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Gift, ExternalLink, Users, DollarSign } from "lucide-react";

const CAT_LABEL = {
  equipment: "Equipment", products: "Lawn Products", furniture: "Outdoor Furniture",
  insurance: "Insurance", funding: "Business Funding", uniforms: "Uniforms", software: "Software", general: "Partner",
};

export default function Affiliate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);

  useEffect(() => { api.get("/affiliate/offers").then((r) => setOffers(r.data.offers)).catch(() => {}); }, []);

  const click = (offerId) => window.open(`${API}/affiliate/click/${offerId}?ref=${user?.referral_code || "guest"}`, "_blank");

  return (
    <Layout>
      <div className="hero-gradient grain text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-semibold"><Gift className="w-3.5 h-3.5 text-gold" /> Affiliate & Referral Program</span>
          <h1 className="font-display text-5xl font-extrabold mt-5">Earn while you grow the lawn community</h1>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Refer customers and providers to earn reward credits, and promote curated partner offers on equipment, products, insurance, and more.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button data-testid="affiliate-referral-btn" onClick={() => navigate(user ? "/referral" : "/signup")} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold gap-2"><Users className="w-4 h-4" /> Get Your Referral Link</Button>
            <Button data-testid="affiliate-join-btn" onClick={() => navigate("/signup")} variant="outline" className="rounded-full bg-white/5 border-white/20 text-white hover:bg-white/10 font-semibold">Join Now</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid md:grid-cols-3 gap-6 -mt-6">
        {[
          { icon: Users, t: "Refer & Earn", d: "$10 credit per customer, more for providers who subscribe." },
          { icon: DollarSign, t: "Partner Commissions", d: "Earn on equipment, insurance, funding & software referrals." },
          { icon: Gift, t: "Reward Credits", d: "Use credits toward bookings or cash out at $50." },
        ].map((c, i) => (
          <div key={i} className="bg-white border border-border rounded-2xl p-6">
            <div className="w-11 h-11 rounded-xl bg-lawn-light flex items-center justify-center mb-3"><c.icon className="w-5 h-5 text-lawn-dark" /></div>
            <h3 className="font-display font-bold text-lawn-ink">{c.t}</h3>
            <p className="text-sm text-muted-foreground mt-1">{c.d}</p>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <h2 className="font-display text-3xl font-extrabold text-lawn-ink">Partner offers</h2>
        <p className="text-muted-foreground mt-1">Hand-picked deals for homeowners and landscaping pros.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {offers.map((o) => (
            <div key={o.offer_id} data-testid={`offer-${o.offer_id}`} className="card-hover bg-white border border-border rounded-2xl p-6 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gold-dark bg-gold-light px-2 py-1 rounded-full">{CAT_LABEL[o.category] || o.category}</span>
                <span className="text-[11px] text-lawn font-semibold">{o.audience === "all" ? "Everyone" : o.audience}</span>
              </div>
              <h3 className="font-display font-bold text-lawn-ink">{o.title}</h3>
              <p className="text-sm text-muted-foreground mt-1 flex-1">{o.description}</p>
              {o.commission && <p className="text-xs text-lawn font-semibold mt-2">💰 {o.commission}</p>}
              <Button data-testid={`offer-click-${o.offer_id}`} onClick={() => click(o.offer_id)} variant="outline" className="mt-4 rounded-full font-semibold gap-2">Visit Offer <ExternalLink className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
